/**
 * Ajustes → Integraciones. F29 #5: Google Calendar.
 *
 * Muestra el estado de la integración (configurada o no), las conexiones
 * activas del usuario actual y permite conectar, desconectar y disparar un
 * sync manual. El "Conectar" no usa la fetch normal: navega al endpoint del
 * backend, que devuelve 302 al consent de Google — por eso usamos
 * window.location.href en vez de api.get.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  CalendarCheck2,
  CheckCircle2,
  Link as LinkIcon,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog } from '../../lib/dialogs'

interface ConnectedConnection {
  id: string
  provider: 'GOOGLE' | 'MICROSOFT'
  externalId: string
  lastSyncAt: string | null
  createdAt: string
}

interface CalendarIntegrationStatus {
  enabled: boolean
  providers: readonly ('GOOGLE' | 'MICROSOFT')[]
  connectedConnections: ConnectedConnection[]
}

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export default function IntegrationsPage() {
  const { t } = useTranslation('settings')
  const [status, setStatus] = useState<CalendarIntegrationStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const formatDate = useCallback(
    (iso: string | null): string => {
      if (!iso) return t('integrations.never')
      try {
        return new Date(iso).toLocaleString(currentLocaleTag(), {
          dateStyle: 'short',
          timeStyle: 'short',
        })
      } catch {
        return iso
      }
    },
    [t]
  )

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get<CalendarIntegrationStatus>(
        '/integrations/calendar/status'
      )
      setStatus(s)
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('integrations.errorNetwork')))
    } finally {
      setLoading(false)
    }
  }, [t])

  // Polling al montar; además detectamos los flags que el callback de OAuth
  // añade a la query (?calendar=connected | ?calendar_error=...) para mostrar
  // feedback inmediato tras el redirect.
  useEffect(() => {
    void loadStatus()
    const url = new URL(window.location.href)
    if (url.searchParams.get('calendar') === 'connected') {
      setOkMsg(t('integrations.connectedOk'))
      url.searchParams.delete('calendar')
      window.history.replaceState({}, '', url.toString())
    }
    const errCode = url.searchParams.get('calendar_error')
    if (errCode) {
      setError(t('integrations.connectError', { code: errCode }))
      url.searchParams.delete('calendar_error')
      window.history.replaceState({}, '', url.toString())
    }
  }, [loadStatus, t])

  const onConnectGoogle = () => {
    // Navegación directa: el backend responde 302 a accounts.google.com.
    // No vale fetch() porque seguiría el redirect en background y el browser
    // no llegaría a ver el consent screen.
    window.location.href = `${API_BASE}/integrations/calendar/google/connect`
  }

  const onDisconnect = async (connectionId: string) => {
    if (!(await confirmDialog({ message: t('integrations.confirmDisconnect'), tone: 'danger' }))) {
      return
    }
    setBusyId(connectionId)
    setError(null)
    setOkMsg(null)
    try {
      await api.delete<void>(`/integrations/calendar/${connectionId}`)
      setOkMsg(t('integrations.disconnectedOk'))
      await loadStatus()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('integrations.errorDisconnect')))
    } finally {
      setBusyId(null)
    }
  }

  const onSyncNow = async (connectionId: string) => {
    setBusyId(connectionId)
    setError(null)
    setOkMsg(null)
    try {
      const r = await api.post<{
        ok: boolean
        imported: number
        updated: number
        deleted: number
        fullSync: boolean
      }>(`/integrations/calendar/${connectionId}/sync`)
      setOkMsg(
        t('integrations.syncOk', {
          imported: r.imported,
          updated: r.updated,
          deleted: r.deleted,
        })
      )
      await loadStatus()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('integrations.errorSync')))
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t('integrations.loading')}
      </div>
    )
  }

  const googleConnections =
    status?.connectedConnections.filter((c) => c.provider === 'GOOGLE') ?? []

  return (
    <div className="space-y-4 max-w-3xl">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">{t('integrations.title')}</h2>
        <p className="text-sm t-muted mt-1">
          {t('integrations.subtitle')}
        </p>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {okMsg && (
        <div className="surface p-3 text-sm rounded-lg border text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40 flex items-center gap-2">
          <CheckCircle2 size={14} /> {okMsg}
        </div>
      )}

      {/* ---------- Calendario ---------- */}
      <section className="surface p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-jr2-500/15 p-2">
            <CalendarCheck2 size={18} className="text-jr2-700 dark:text-jr2-200" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="t-primary font-semibold">{t('integrations.calendar.title')}</h3>
            <p className="text-xs t-muted mt-0.5">
              {t('integrations.calendar.subtitle')}
            </p>
          </div>
        </div>

        {!status?.enabled ? (
          <p className="text-xs t-faint border border-base rounded-lg p-3">
            {t('integrations.calendar.notConfiguredPre')}
            <code className="px-1 mx-1 rounded bg-[var(--bg-input)]">GOOGLE_CALENDAR_CLIENT_ID</code>,
            <code className="px-1 mx-1 rounded bg-[var(--bg-input)]">GOOGLE_CALENDAR_CLIENT_SECRET</code>
            {' '}{t('integrations.calendar.and')}{' '}
            <code className="px-1 mx-1 rounded bg-[var(--bg-input)]">GOOGLE_CALENDAR_REDIRECT_URI</code>
            {' '}{t('integrations.calendar.notConfiguredPost')}
          </p>
        ) : googleConnections.length === 0 ? (
          <div className="flex items-center justify-between gap-3 border border-base rounded-lg p-3">
            <div className="text-sm t-secondary">
              {t('integrations.calendar.noAccounts')}
            </div>
            <button
              type="button"
              onClick={onConnectGoogle}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5 whitespace-nowrap"
            >
              <LinkIcon size={14} /> {t('integrations.calendar.connectGoogle')}
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700/40 border border-base rounded-lg">
            {googleConnections.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-3 p-3 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="t-primary font-medium truncate">
                    Google · {c.externalId}
                  </div>
                  <div className="text-xs t-muted mt-0.5">
                    {t('integrations.calendar.lastSync')}: {formatDate(c.lastSyncAt)} · {t('integrations.calendar.connectedAt')}:{' '}
                    {formatDate(c.createdAt)}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => void onSyncNow(c.id)}
                  className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-50"
                >
                  {busyId === c.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <RefreshCw size={12} />
                  )}
                  {t('integrations.calendar.syncNow')}
                </button>
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => void onDisconnect(c.id)}
                  className="px-2.5 py-1.5 rounded-lg text-xs border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Trash2 size={12} /> {t('integrations.calendar.disconnect')}
                </button>
              </li>
            ))}
            <li className="p-3 border-t border-base">
              <button
                type="button"
                onClick={onConnectGoogle}
                className="text-xs t-secondary hover:t-primary inline-flex items-center gap-1.5"
              >
                <LinkIcon size={12} /> {t('integrations.calendar.connectAnother')}
              </button>
            </li>
          </ul>
        )}
      </section>
    </div>
  )
}
